import { useCallback, useEffect } from 'react';
import type { RefObject } from 'react';

import type { CropperCanvas, CropperImage } from 'cropperjs';

import type { CropperInstance } from '#src/app/shared/hooks/imageUpload';

import { getImageScale } from '../coverTransform';

type Props = {
	canvasRef: RefObject<CropperCanvas | null>;
	imageRef: RefObject<CropperImage | null>;
	image?: string;
	onInitialized?: (instance: CropperInstance) => void;
};

/**
 * Returns a callback that renders the currently visible viewport at native image resolution
 * (matching the source photo's own pixel density, rather than however large the on-screen
 * viewport happens to be), and exposes it to `onInitialized` once per image.
 */
export function useCroppedCanvas({
	canvasRef,
	imageRef,
	image,
	onInitialized,
}: Props): () => Promise<HTMLCanvasElement | undefined> {
	const getCroppedCanvas = useCallback(async () => {
		const canvas = canvasRef.current;
		const scale = getImageScale(imageRef.current);
		if (!canvas || !scale) return undefined;
		return canvas.$toCanvas({
			width: canvas.offsetWidth / scale,
			height: canvas.offsetHeight / scale,
		});
	}, [canvasRef, imageRef]);

	useEffect(() => {
		if (onInitialized) {
			onInitialized({
				getCroppedImageDataUrl: async () => {
					const croppedCanvas = await getCroppedCanvas();
					return croppedCanvas?.toDataURL() ?? '';
				},
			});
		}
		// only run once, when the image first becomes ready
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [image]);

	return getCroppedCanvas;
}
